import React, { useState, useEffect } from 'react';
import { TOPBAR_H } from '../../config/layout';

/**
 * 달력 한 칸의 최소 높이 (2026-09-23)
 *
 * 한 칸에 최대 3건을 보여준다. 3건이 잘리지 않으려면:
 *   안쪽 여백 12 + 날짜줄 20 + 줄간격 12 + 건당 34 x 3 = 147
 * 예전에는 행 높이가 `1fr` 뿐이라, 6주짜리 달[예: 2026년 8월]에서는
 * 화면 높이에 눌려 행이 120px 아래로 줄고 3번째 건이 잘려 나갔다.
 */
const MIN_ROW_H = 150;

const AsReserve = ({ isOpen, onClose, records = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [rel, setRel] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) setPos({ x: 0, y: 0 });
  }, [isOpen]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPos({ x: e.clientX - rel.x, y: e.clientY - rel.y });
    };
    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, rel]);

  if (!isOpen) return null;

  const handleMouseDown = (e) => {
    if (e.target.tagName.toLowerCase() === 'button') return;
    setIsDragging(true);
    setRel({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  const getHolidayName = (year, month, day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const mmdd = dateStr.substring(5);
    
    const fixedHolidays = {
      '01-01': '신정', '03-01': '삼일절', '05-05': '어린이날', '06-06': '현충일',
      '08-15': '광복절', '10-03': '개천절', '10-09': '한글날', '12-25': '성탄절'
    };
    const variableHolidays = {
      '2026-02-16': '설연휴', '2026-02-17': '설날', '2026-02-18': '설연휴', '2026-03-02': '대체공휴일', 
      '2026-05-24': '부처님오신날', '2026-05-25': '대체공휴일', 
      '2026-09-24': '추석연휴', '2026-09-25': '추석', '2026-09-26': '추석연휴'
    };
    return variableHolidays[dateStr] || fixedHolidays[mmdd] || null;
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay(); 
  const daysInMonth = new Date(year, month + 1, 0).getDate(); 
  
  const totalWeeks = Math.ceil((firstDay + daysInMonth) / 7);
  const totalCells = totalWeeks * 7; 

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const getRecordsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return records.filter(r => r.asDate === dateStr || r.settleDate === dateStr);
  };

  const getBadgeStyle = (status) => {
    if (status === '예약') return { background: '#f3e8ff', color: '#9333ea', border: '1px solid #d8b4fe' }; 
    if (status === '완료') return { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }; 
    if (status === '진행중') return { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }; 
    return { background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' }; 
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', pointerEvents: 'auto' }} onClick={onClose} />
      
      <div style={{
        // 상단 바를 덮지 않도록 그 아래에서 시작해 화면 끝까지 쓴다.
        // 예전에는 height:90vh 를 화면 한가운데 두어서, 화면이 1280px 보다 낮으면
        // 위로는 상단 바를 가리고 아래로는 마지막 주가 잘려 나갔다.
        position: 'absolute', top: `${TOPBAR_H + 12}px`, bottom: '12px', left: '50%',
        transform: `translate(calc(-50% + ${pos.x}px), ${pos.y}px)`,
        background: 'white', width: '90%', maxWidth: '1400px',
        borderRadius: '16px', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        pointerEvents: 'auto'
      }}>
        
        <div 
          onMouseDown={handleMouseDown} 
          style={{ padding: '16px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: '16px 16px 0 0', cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', pointerEvents: 'none' }}>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>🗓️ AS 예약 및 작업 현황</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', pointerEvents: 'auto' }}>
              <button onClick={prevMonth} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white', cursor: 'pointer' }}>◀</button>
              <span style={{ fontSize: '18px', fontWeight: '700', width: '120px', textAlign: 'center' }}>{year}년 {month + 1}월</span>
              <button onClick={nextMonth} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white', cursor: 'pointer' }}>▶</button>
              <button onClick={goToday} style={{ padding: '6px 16px', border: 'none', borderRadius: '8px', background: '#e2e8f0', cursor: 'pointer', fontWeight: 'bold', marginLeft: '10px' }}>오늘</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', pointerEvents: 'auto' }}>
            <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: 'bold' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#f3e8ff', border: '1px solid #d8b4fe', borderRadius: '3px' }}></div>예약</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '3px' }}></div>접수</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '3px' }}></div>진행중</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '3px' }}></div>완료</span>
            </div>
            <button onClick={onClose} style={{ padding: '8px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>닫기 ✖</button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f1f5f9', padding: '0 10px 10px', overflowY: 'auto' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', background: '#cbd5e1', border: '1px solid #cbd5e1', position: 'sticky', top: 0, zIndex: 1 }}>
            {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => (
              <div key={day} style={{ padding: '8px', textAlign: 'center', background: 'white', fontWeight: 'bold', color: (idx === 0 || idx === 6) ? '#ef4444' : '#475569' }}>
                {day}
              </div>
            ))}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${totalWeeks}, minmax(${MIN_ROW_H}px, 1fr))`, gap: '1px', background: '#cbd5e1', border: '1px solid #cbd5e1', borderTop: 'none', flex: 1 }}>
            
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-start-${i}`} style={{ background: '#f8fafc' }}></div>)}
            
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dailyRecords = getRecordsForDate(day);
              const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
              
              const dayOfWeek = (firstDay + i) % 7; 
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; 
              const holidayName = getHolidayName(year, month + 1, day);
              const isRedDay = isWeekend || holidayName;

              return (
                <div key={day} style={{ background: isToday ? '#fffae6' : 'white', padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px', overflow: 'hidden' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: isRedDay ? '#ef4444' : '#0f172a' }}>
                      {day} {holidayName && <span style={{ fontSize: '11px', fontWeight: 'normal', marginLeft: '4px' }}>{holidayName}</span>}
                    </span>
                    {dailyRecords.length > 0 && (
                      <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', background: '#e2e8f0', padding: '2px 6px', borderRadius: '10px' }}>
                        총 {dailyRecords.length}건
                      </span>
                    )}
                  </div>

                  {/* 🚀 최대 3개까지만 출력. 시간(r.asTime)이 존재할 경우 차량번호/고객명 앞에 예쁘게 동적 표기 */}
                  {dailyRecords.slice(0, 3).map(r => (
                    <div key={r.id} style={{ ...getBadgeStyle(r.status), padding: '3px 6px', borderRadius: '4px', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: '1.2' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                        <span>[{r.status || '접수'}]</span>
                        <span style={{ color: '#475569' }}>{r.mechanic}</span>
                      </div>
                      <div style={{ fontWeight: '800', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.asTime ? `[${r.asTime}] ` : ''}{r.carNumber || r.customerName}
                      </div>
                    </div>
                  ))}
                  
                </div>
              );
            })}

            {Array.from({ length: totalCells - firstDay - daysInMonth }).map((_, i) => <div key={`empty-end-${i}`} style={{ background: '#f8fafc' }}></div>)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsReserve;